import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  Radio
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/system';
import CardMedia from '@mui/material/CardMedia';
import { useTranslation } from 'react-i18next';

const PartnerSelection = ({
  partner,
  onPartnerChange,
  resetPartner,
  parentSelectedPartners
}) => {
  const [selectedPartners, setSelectedPartners] = useState<string[]>([]);
  const [showAll, setShowAll] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { t } = useTranslation();

  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));

  const partnersData = partner || [];
  const categories = Array.isArray(t('capaDropdown', { returnObjects: true }))
    ? t('capaDropdown', { returnObjects: true })
    : Object.values(t('capaDropdown', { returnObjects: true }));

  const handleClick = (partner: any, index: number) => {
    if (selectedPartners.includes(partner.name)) {
      setSelectedPartners((prev) => prev.filter((p) => p !== partner.name));
      const partnerIndex = selectedPartners.findIndex(
        (item) => item === partner.name
      );
      onPartnerChange(null, partnerIndex);
      setErrorMessage(null); // Clear error message when deselecting
    } else if (selectedPartners.length <= 3) {
      const partnerIndex = selectedPartners.findIndex((item) => item === '');
      if (partnerIndex === -1) {
        const msg = t('capabilitiesFunctionHeader.errorMsg');
        setErrorMessage(msg);
      } else {
        setSelectedPartners((prev) => [...prev, partner.name]);
        onPartnerChange(partner, [partnerIndex]);
        setErrorMessage(null); // Clear error message on valid selection
      }
    } else {
      const msg = t('capabilitiesFunctionHeader.errorMsg');
      setErrorMessage(null);
      setTimeout(() => setErrorMessage(msg), 0);
    }
  };

  const handleFilterToggle = (filter: string) => {
    let updatedFilters: string[];
    if (selectedFilters.includes(filter)) {
      updatedFilters = selectedFilters.filter((f) => f !== filter);
    } else {
      updatedFilters = [...selectedFilters, filter];
    }
    setSelectedFilters(updatedFilters);
    setShowAll(updatedFilters.length === 0);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLLIElement>,
    value: string
  ) => {
    if (event.key === ' ') {
      let updatedFilters: string[];
      if (selectedFilters.includes(value)) {
        updatedFilters = selectedFilters.filter((f) => f !== value);
      } else {
        updatedFilters = [...selectedFilters, value];
      }
      setSelectedFilters(updatedFilters);
      setShowAll(updatedFilters.length === 0);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setSelectedFilters([]); // Undo selected values
      setShowAll(true); // Reset to "Show all"
      const dropdown = event.currentTarget as HTMLElement;
      dropdown.blur(); // Close the dropdown
    } else if (event.key === 'Enter') {
      event.preventDefault(); // Prevent Enter from toggling selection

      // event.stopPropagation(); // Stop propagation to parent handlers
    }
  };

  const handleDropdownKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent default behavior
      event.stopPropagation(); // Stop the event from propagating further

      // Close the dropdown programmatically
      const dropdown = event.currentTarget.closest('.MuiPopover-root');
      if (dropdown) {
        const backdrop = dropdown.querySelector(
          '.MuiBackdrop-root'
        ) as HTMLElement;
        if (backdrop) {
          backdrop.click(); // Simulate a click on the backdrop to close the dropdown
        }
      }
    }
  };

  const handleShowAll = () => {
    setShowAll(true);
    setSelectedFilters([]);
  };

  const filteredPartners = showAll
    ? partnersData
    : partnersData.filter((p) => selectedFilters.includes(p.type));

  useEffect(() => {
    if (partnersData.length > 0 && selectedPartners.length === 0) {
      const initialSelection = partnersData.slice(0, 3).map((partner) => ({
        name: partner.name,
        logo_name: partner.logo_name
      }));
      setSelectedPartners(initialSelection.map((partner) => partner.name));
      initialSelection.forEach((partner, index) =>
        onPartnerChange(
          {
            name: partner.name,
            logo_name: partner.logo_name
          },
          index
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnersData]);

  useEffect(() => {
    if (resetPartner === 'reset') {
      const initialSelection = partnersData.slice(0, 3).map((partner) => ({
        name: partner.name,
        logo_name: partner.logo_name
      }));
      setSelectedPartners(initialSelection.map((partner) => partner.name));
      initialSelection.forEach((partner, index) =>
        onPartnerChange(
          {
            name: partner.name,
            logo_name: partner.logo_name
          },
          index
        )
      );
    } else if (resetPartner) {
      setSelectedPartners((prev) =>
        prev.filter((partner) => partner !== resetPartner)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetPartner]);

  useEffect(() => {
    const selectedArray = parentSelectedPartners?.map(
      (item) => item.partnerURL
    );
    selectedArray && setSelectedPartners(selectedArray);
  }, [parentSelectedPartners]);

  return (
    <Box p={1} pr={2} mb={8}>
      <Box
        id="updateNotice"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(1px, 1px, 1px, 1px)',
          whiteSpace: 'nowrap'
        }}
        aria-live="polite"
        aria-atomic="true"
      >
        {t('capabilitiesFunctionHeader.ariaUpdateNotice')}
      </Box>
      <Box
        display="flex"
        flexDirection={isTabletOrMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isTabletOrMobile ? 'flex-start' : 'center'}
        aria-labelledby="Partner-selection-group"
        // mb={2}
      >
        {/* Grouped Header and Subtitle */}
        <Box sx={{ mb: isTabletOrMobile ? 2 : 0 }}>
          <Typography
            id="Partner-selection-group"
            variant="h2"
            sx={{
              fontFamily: 'Open Sans',
              fontWeight: 700,
              fontSize: isTabletOrMobile ? '20px' : '28px',
              lineHeight: isTabletOrMobile ? '28px' : '36px',
              color: '#000000',
              mb: 1 // Add spacing for mobile view
            }}
          >
            {t('capabilitiesFunctionHeader.header')}
          </Typography>
          <Typography
            component="h3"
            variant="body1"
            sx={{
              fontFamily: 'Open Sans',
              fontWeight: '600 !important',
              fontSize: isTabletOrMobile
                ? '14px !important'
                : '16px !important',
              lineHeight: '20px !important',
              color: '#000000'
              // mb: 1
            }}
          >
            {t('capabilitiesFunctionHeader.subtitle')}
          </Typography>
        </Box>

        {/* Filter Function */}
        <Box
          role="group"
          aria-labelledby="filter-by-business-Model"
          sx={{
            alignSelf: 'baseline',
            mt: isTabletOrMobile ? -4 : 0,
            mb: isTabletOrMobile ? 6 : 0
          }}
        >
          <FormControl sx={{ minWidth: 300, maxWidth: 304 }} size="small">
            <Select
              multiple
              inputProps={{
                'aria-label': t('capabilitiesFunctionHeader.dropdownPlaceholer')
              }}
              variant="standard"
              value={showAll ? ['Show all'] : selectedFilters}
              onChange={() => { }}
              renderValue={(selected) => (
                <span
                  style={{
                    fontFamily: 'Open Sans',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '20px',
                    color: '#000000'
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      width: '1px',
                      height: '1px',
                      overflow: 'hidden',
                      clip: 'rect(1px, 1px, 1px, 1px)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Selected:
                  </span>
                  {(selectedFilters as string[]).join(', ') || (
                    <span aria-hidden="true">
                      {' '}
                      {t('capabilitiesFunctionHeader.dropdownPlaceholer')}{' '}
                    </span>
                  )}
                </span>
              )}
              MenuProps={{
                PaperProps: {
                  onKeyDownCapture: handleDropdownKeyDown // Handle Enter key to close dropdown
                },
                disableScrollLock: true
              }}
              startAdornment={
                <FilterListIcon sx={{ mr: 1, color: '#1460AA' }} />
              }
              aria-labelledby="filter-label"
              aria-multiselectable="true"
              aria-expanded={false}
              aria-haspopup="listbox"
              // aria-label={t('capabilitiesFunctionHeader.dropdownPlaceholer')}
              aria-describedby="updateNotice"
            >
              <MenuItem
                value="Show all"
                onClick={handleShowAll}
                autoFocus
                onKeyDown={(event) => {
                  if (event.key === ' ') {
                    event.preventDefault(); // Prevent default scrolling behavior
                    handleShowAll(); // Trigger the showAll action
                  }
                }}
                role="option"
                aria-checked={showAll}
              >
                <Radio aria-hidden={true} checked={showAll} />
                <ListItemText
                  primary={t('capabilitiesFunctionHeader.menuItem')}
                  aria-labelledby="filter by Business Model"
                />
              </MenuItem>

              {Array.isArray(categories) &&
                categories
                  .filter((cat) => cat.name !== 'Show all')
                  .map((cat) => (
                    <MenuItem
                      key={cat.name}
                      value={cat.name}
                      onClick={() => handleFilterToggle(cat.name)}
                      onKeyDown={(event) => handleKeyDown(event, cat.name)}
                      role="option"
                      aria-checked={selectedFilters.includes(cat.name)}
                      aria-selected={selectedFilters.includes(cat.name)}
                    >
                      <Checkbox
                        checked={selectedFilters.includes(cat.name)}
                        aria-hidden="true"
                        disabled={showAll}
                      />
                      <ListItemText
                        primary={cat.name}
                        aria-labelledby="filter by Business Model"
                      />
                    </MenuItem>
                  ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Render Filter Function Below Header for Mobile */}
      {/* {isTabletOrMobile && (
        <Box mb={2}>
          {filterFunction}
        </Box>
      )} */}

      {errorMessage && (
        <Typography
          role="alert" // Announce error to screen readers
          sx={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
            clip: 'rect(1px, 1px, 1px, 1px)',
            whiteSpace: 'nowrap'
          }}
        >
          {errorMessage}
        </Typography>
      )}

      <fieldset
        style={{
          border: 'none',
          padding: 0,
          margin: 0
        }}
      >
        <legend
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
            clip: 'rect(1px, 1px, 1px, 1px)',
            whiteSpace: 'nowrap'
          }}
        >
          {t('capabilitiesFunctionHeader.header')}
        </legend>

        <Grid
          container
          spacing={2}
          aria-describedby="updateNotice"
        >
          {filteredPartners.map((partner, index) => (
            <Grid item xs={6} sm={3} md={3} lg={2} key={partner.name} sx={{}}>
              <Box
                role="button"
                aria-pressed={selectedPartners.includes(partner.name)}
                 aria-label={`Select ${partner.logo_name} partner`}
                aria-describedby="updateNotice"
                sx={{
                  border: '1px solid #C9CACC',
                  borderColor: selectedPartners.includes(partner.name)
                    ? '#1460AA'
                    : '#C9CACC',
                  bgcolor: selectedPartners.includes(partner.name)
                    ? '#F1F7FB'
                    : '#FFFFFF',
                  borderRadius: '8px',
                  m: '0 auto',
                  height: '64px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: partner.name.toLowerCase().includes("kyte") ? 0: '23px',
                  cursor: 'pointer',
                  outline: 'none'
                }}
                tabIndex={0}
                onClick={() => handleClick(partner, index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick(partner, index);
                    const element = document.activeElement as HTMLElement;
                    if (element) element.focus();
                  }
                }}
              >
                {partner.isText ? (
                  <Typography
                    sx={{
                      fontFamily: 'Open Sans',
                      fontWeight: 700,
                      fontSize: '18px',
                      lineHeight: '24px',
                      color: '#000000',
                      bgcolor: selectedPartners.includes(partner.name)
                        ? '#F1F7FB'
                        : '#FFFFFF'
                    }}
                  >
                    {partner.name}
                  </Typography>
                ) : (
                  <CardMedia
                    component="img"
                    sx={{
                      objectFit: 'scale-down',
                      height: '60px'
                    }}
                    src={partner.name}
                    alt={partner.alt}
                  />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </fieldset>

    </Box>
  );
};

export default PartnerSelection;
